/**
 * @file Действия скачивания для экрана папки.
 *
 * Передаёт имя и путь в DownloadProvider, а его состояние превращает
 * в признаки занятости строк. Завершённая передача обновляет показанную квоту.
 */
import { useEffect, useRef, useState } from 'react'

import { downloadMessages } from '../features/downloads/messages'
import { isDownloadBusy } from '../features/downloads/model'
import { useDownload } from '../features/downloads/useDownload'
import { useLocale } from '../i18n/useLocale'

type Params = {
    currentFolder: string
    password: string
    boxID: string
    token?: string
    refreshQuota: () => void
}

export function useFileTransfer({
    currentFolder,
    password,
    boxID,
    token,
    refreshQuota,
}: Params) {
    const { start: startDownload, state } = useDownload()
    const { locale } = useLocale()

    // blocked показывает отказ начать вторую задачу, refreshed предотвращает
    // повторное обновление квоты при каждом отображении завершённой задачи.
    const [blocked, setBlocked] = useState(false)
    const refreshed = useRef<string | null>(null)

    const busy = isDownloadBusy(state)

    useEffect(() => {
        if (
            !state ||
            busy ||
            state.boxId !== boxID ||
            refreshed.current === state.id
        )
            return

        refreshed.current = state.id
        refreshQuota()
    }, [state, busy, boxID, refreshQuota])

    function start(kind: 'file' | 'folder', name: string) {
        if (!token) return

        setBlocked(
            !startDownload({
                kind,
                name,
                path: `${currentFolder}/${name}`,
                password,
                boxId: boxID,
                token,
            }),
        )
    }

    const active = busy && state?.boxId === boxID ? state : null

    return {
        archivingFolders:
            active?.phase === 'preparing' ? [active.sourcePath] : [],
        downloadingFiles:
            active && active.phase !== 'preparing' ? [active.sourcePath] : [],
        notice: blocked && busy ? downloadMessages[locale].busy : null,
        downloadFile: (name: string) => start('file', name),
        downloadFolder: (name: string) => start('folder', name),
    }
}
